// THE CUSTOMER BOOK'S BULK BAR, as a run over single routes (spec 09 §4.1's
// E2-2a1 annotation, D-323 ruling 7). Pure apart from the sender it is handed:
// the screen supplies `send`, the clock and the sleep, and a test supplies
// fakes for all three.
//
// ONE KEY PER (RUN, ROW). `startBulkRun` mints one Idempotency-Key for every
// row it is given, and the row keeps it for the life of the run. Retry failed
// resends a `failed` row under that same key, which is what makes the retry
// safe: the server's idempotency layer releases a key on a 5xx and replays the
// stored answer after a lost response (`apps/api/src/platform/
// idempotency.interceptor.ts`), so a request that did write is answered
// rather than written again. A new run over the same rows mints new keys.
//
// FOUR OUTCOMES, AND WHICH ONES COME BACK.
//
//   done              a sent row answered 2xx: the row now holds the state the
//                     verb asked for.
//   already           the loaded row already answered the verb, so nothing was
//                     sent (Add tag on a row carrying the tag, Remove tag on
//                     one lacking it, Archive on an archived row, the three
//                     routes that answer the same 200 whether or not they
//                     wrote), or the server said so in a code the verb reads
//                     as settled (`ASSIGNMENT_EXISTS` for Reassign owner).
//   refused:<CODE>    a 4xx. Never resent under its key, because the layer
//                     stores a 4xx and would replay the same refusal after its
//                     cause is fixed (`idempotency.interceptor.ts:269-275`).
//   failed            no answer (a lost response), a 5xx such as 503
//                     `RESOURCE_BUSY`, 409 `IDEMPOTENCY_IN_PROGRESS`, the one
//                     4xx that is no refusal (the first request under the key
//                     may still be running), or a 401. Retry failed resends
//                     these only, and an in-progress row no sooner than the
//                     answer's `Retry-After`, so the layer replays the first
//                     request's answer once it lands.
//
// A 401 STOPS THE RUN. The session is gone, so every later row would meet the
// same 401. The row that met it is `failed` (no key is stored on a 401, so a
// resend after signing in is safe), the rows after it stay unsent and count
// as not sent, and the run comes back marked `halted: "unauthorized"` so the
// screen sends the reader to sign in, as every read on it does.
//
// SEQUENTIAL, NOT PARALLEL. One row at a time: a selection of fifty is fifty
// holds on fifty customer rows, and running them together would turn the
// server's lock queue into our own `RESOURCE_BUSY` refusals.
//
// WHAT STAYS SELECTED. The refused and the failed rows, so the reader can see
// which ones and press Retry failed or act on the record itself. The done and
// already rows leave the selection.

import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, newIdempotencyKey } from "../../../../lib/request";

export type BulkVerb = "add_tag" | "remove_tag" | "reassign_owner" | "archive";

export type BulkOutcome =
  | { readonly kind: "done" }
  | { readonly kind: "already" }
  | { readonly kind: "refused"; readonly code: string }
  | {
      readonly kind: "failed";
      /** The earliest instant (ms) a retry may resend this row: set from the
       * `Retry-After` of a 409 `IDEMPOTENCY_IN_PROGRESS`. */
      readonly notBefore?: number;
    };

export interface BulkRow {
  readonly id: string;
  /** This row's Idempotency-Key for the whole run. */
  readonly key: string;
  readonly outcome?: BulkOutcome;
}

export interface BulkRun {
  readonly verb: BulkVerb;
  /** The verb's object: a tag id, or the new owner's user id. Archive has
   * none. */
  readonly target: string | undefined;
  readonly rows: readonly BulkRow[];
  /** Set when a 401 stopped the run part way (the header says why). */
  readonly halted?: "unauthorized";
}

/** The fields of a book row the verbs judge, as the list serves them. */
export interface BulkRowState {
  readonly id: string;
  readonly status: string;
  readonly owner_user_id: string | null;
  readonly tags: readonly { readonly id: string }[];
}

/** When the server answers no `Retry-After` on an in-progress refusal, wait
 * this long before resending under the same key. */
export const IN_PROGRESS_DEFAULT_WAIT_MS = 1000;

/** The codes each verb reads as "already so" rather than as a refusal. */
const ALREADY_CODES: Readonly<Record<BulkVerb, ReadonlySet<string>>> = {
  add_tag: new Set(),
  remove_tag: new Set(),
  // The user already holds the role sent: a hand-over another admin made to
  // the same person reads as settled (D-326 ruling 3).
  reassign_owner: new Set(["ASSIGNMENT_EXISTS"]),
  archive: new Set(),
};

/** A new run: one fresh key per row. `mintKey` is injectable for tests. */
export function startBulkRun(
  verb: BulkVerb,
  target: string | undefined,
  rowIds: readonly string[],
  mintKey: () => string = newIdempotencyKey,
): BulkRun {
  return { verb, target, rows: rowIds.map((id) => ({ id, key: mintKey() })) };
}

/** Does the row's loaded state already answer the verb, so that nothing need
 * be sent? Reassign owner is never judged here: the route answers
 * `ASSIGNMENT_EXISTS` itself, under the customer's lock, which is truer than a
 * page the reader loaded a minute ago. */
export function loadedStateAnswers(
  verb: BulkVerb,
  target: string | undefined,
  row: BulkRowState,
): boolean {
  switch (verb) {
    case "add_tag":
      return target !== undefined && row.tags.some((tag) => tag.id === target);
    case "remove_tag":
      return target !== undefined && !row.tags.some((tag) => tag.id === target);
    case "archive":
      return row.status === "archived";
    case "reassign_owner":
      return false;
  }
}

/** One answer as an outcome. */
export function classifyBulkError(verb: BulkVerb, error: unknown, nowMs: number): BulkOutcome {
  if (!(error instanceof RequestError)) {
    // No problem document: a lost response, a network cut, or a 401, which
    // `executeBulk` also takes as the end of the run. Nothing was stored
    // under the key that could refuse a resend.
    return { kind: "failed" };
  }
  if (error.httpStatus >= 500) return { kind: "failed" };
  if (error.code === "IDEMPOTENCY_IN_PROGRESS") {
    return {
      kind: "failed",
      notBefore: nowMs + (error.retryAfterMs ?? IN_PROGRESS_DEFAULT_WAIT_MS),
    };
  }
  if (error.code !== undefined && ALREADY_CODES[verb].has(error.code)) return { kind: "already" };
  if (error.httpStatus >= 400) {
    return { kind: "refused", code: error.code ?? `HTTP_${String(error.httpStatus)}` };
  }
  return { kind: "failed" };
}

export interface BulkDeps {
  /** Sends one row's request under its key; resolves on 2xx, throws the
   * `RequestError` (or anything else) otherwise. */
  readonly send: (rowId: string, key: string) => Promise<unknown>;
  /** The row's loaded state, for the `already` pre-check. A row the book no
   * longer holds is sent. */
  readonly rowState?: (rowId: string) => BulkRowState | undefined;
  readonly now?: () => number;
  readonly sleep?: (ms: number) => Promise<void>;
  /** Called after every row with the run as it stands, for progress. */
  readonly onProgress?: (run: BulkRun) => void;
  /** Stops the run between rows (the screen unmounting, the reader leaving). */
  readonly signal?: AbortSignal;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function withOutcome(run: BulkRun, index: number, outcome: BulkOutcome): BulkRun {
  return {
    ...run,
    rows: run.rows.map((row, i) => (i === index ? { ...row, outcome } : row)),
  };
}

/**
 * Runs the rows that have no outcome yet (a new run), or, with `retry`, the
 * rows whose outcome is `failed`, each under its own key. Answers the run with
 * every row's outcome. A row stopped by `signal` before it was sent keeps the
 * outcome it had (none, or `failed`).
 */
export async function executeBulk(
  run: BulkRun,
  deps: BulkDeps,
  mode: "first" | "retry" = "first",
): Promise<BulkRun> {
  const now = deps.now ?? Date.now;
  const sleep = deps.sleep ?? defaultSleep;
  // A function, not a property read: the signal can abort while a send is in
  // flight, and a narrowed `aborted` would not see it.
  const stopped = (): boolean => deps.signal?.aborted === true;
  // A new pass starts unhalted: a retry after signing in sends again.
  let current: BulkRun = { verb: run.verb, target: run.target, rows: run.rows };
  for (let index = 0; index < current.rows.length; index += 1) {
    if (stopped()) break;
    const row = current.rows[index];
    if (row === undefined) continue;
    const outcome = row.outcome;
    if (mode === "first" && outcome !== undefined) continue;
    if (mode === "retry" && outcome?.kind !== "failed") continue;

    if (mode === "first") {
      const state = deps.rowState?.(row.id);
      if (state !== undefined && loadedStateAnswers(current.verb, current.target, state)) {
        current = withOutcome(current, index, { kind: "already" });
        deps.onProgress?.(current);
        continue;
      }
    }

    if (outcome?.kind === "failed" && outcome.notBefore !== undefined) {
      const wait = outcome.notBefore - now();
      if (wait > 0) await sleep(wait);
      if (stopped()) break;
    }

    let next: BulkOutcome;
    try {
      await deps.send(row.id, row.key);
      next = { kind: "done" };
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        current = { ...withOutcome(current, index, { kind: "failed" }), halted: "unauthorized" };
        deps.onProgress?.(current);
        break;
      }
      next = classifyBulkError(current.verb, error, now());
    }
    current = withOutcome(current, index, next);
    deps.onProgress?.(current);
  }
  return current;
}

export interface BulkSummary {
  readonly total: number;
  readonly done: number;
  readonly already: number;
  readonly failed: number;
  /** Each refusal code with how many rows met it, in the order first met. */
  readonly refused: readonly { readonly code: string; readonly count: number }[];
  /** Rows not yet answered (a run in flight, or one stopped part way). */
  readonly pending: number;
}

export function bulkSummary(run: BulkRun): BulkSummary {
  let done = 0;
  let already = 0;
  let failed = 0;
  let pending = 0;
  const refused = new Map<string, number>();
  for (const row of run.rows) {
    const outcome = row.outcome;
    if (outcome === undefined) pending += 1;
    else if (outcome.kind === "done") done += 1;
    else if (outcome.kind === "already") already += 1;
    else if (outcome.kind === "failed") failed += 1;
    else refused.set(outcome.code, (refused.get(outcome.code) ?? 0) + 1);
  }
  return {
    total: run.rows.length,
    done,
    already,
    failed,
    pending,
    refused: [...refused.entries()].map(([code, count]) => ({ code, count })),
  };
}

/** The rows that stay selected after a run: every refused and failed row. */
export function rowsKeptSelected(run: BulkRun): ReadonlySet<string> {
  return new Set(
    run.rows
      .filter((row) => row.outcome?.kind === "refused" || row.outcome?.kind === "failed")
      .map((row) => row.id),
  );
}

/** Does Retry failed have anything to resend? */
export function hasFailedRows(run: BulkRun): boolean {
  return run.rows.some((row) => row.outcome?.kind === "failed");
}
